import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Header,
  Icon,
  Button,
  Card,
  Segment,
  Input,
  Form,
  TextArea,
  Label,
  Dropdown,
  Message,
  Grid,
  Modal,
  Confirm
} from 'semantic-ui-react';
import {
  sendMessage,
  fetchConversation,
  deleteMessage,
  deleteThread,
  deleteConversation,
  adminHardDeleteMessage,
  adminHardDeleteThread,
  adminHardDeleteConversation
} from '../../slices/messageSlice';
import { hasPermission } from '../../utils/roleUtils';
import useRoleNormalization from '../../hooks/useRoleNormalization';
import './MessagingTest.css';

const MessagingTest = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector(state => state.user.profile.data);

  // Initialize role normalization hook
  const { normalizeRole, getRoleLabel, getRoleIcon, getUserTypeOptions } = useRoleNormalization();

  // Normalize the current user's role
  const userRole = normalizeRole(currentUser?.role || currentUser?.primaryRole) || 'voiceActor';
  const isAdmin = userRole === 'admin';

  console.log("[MESSAGING TEST ENTRY");
  // Test user configurations
  const [testUsers, setTestUsers] = useState({
    voiceActor: '',
    bookCreator: '',
    admin: ''
  });

  // Admin confirmation states
  const [adminConfirmation, setAdminConfirmation] = useState({
    show: false,
    type: '',
    messageId: '',
    conversationId: '',
    threadId: '',
    confirmText: ''
  });

  // Current conversation setup
  const [activeConversation, setActiveConversation] = useState({
    user1: '',
    user2: '',
    user1Type: '',
    user2Type: ''
  });

  // Message states
  const [messages, setMessages] = useState([]);
  const [newMessage1, setNewMessage1] = useState('');
  const [newMessage2, setNewMessage2] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDeletedMessages, setShowDeletedMessages] = useState(false);

  // SSE connection for real-time updates
  const eventSourceRef = useRef(null);
  const messagesEndRef1 = useRef(null);
  const messagesEndRef2 = useRef(null);

  // Initialize SSE connection
  useEffect(() => {
    console.log("[MESSAGE TESTING");
    if (activeConversation.user1 && activeConversation.user2) {
      // Close existing connection
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }

      // Create new SSE connection
      const API_BASE = process.env.REACT_APP_API_URL_RAW || 'http://localhost:5000';
      const eventSource = new EventSource(`${API_BASE}/api/sse-endpoint?userId=${activeConversation.user1}`);
      eventSourceRef.current = eventSource;

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'NEW_MESSAGE') {
            // Add new message to the conversation
            setMessages(prev => [...prev, data.message]);
            scrollToBottom();
          }
        } catch (error) {
          console.error('Error parsing SSE message:', error);
        }
      };

      eventSource.onerror = (error) => {
        console.error('SSE connection error:', error);
        setError('Real-time connection lost. Messages may not update automatically.');
      };

      return () => {
        eventSource.close();
      };
    }
  }, [activeConversation.user1, activeConversation.user2]);

  // Auto-reload conversation when showDeletedMessages toggle changes (admin only)
  useEffect(() => {
    if (isAdmin && activeConversation.user1 && activeConversation.user2 && messages.length > 0) {
      loadConversation();
    }
  }, [showDeletedMessages]);

  // Scroll to bottom of message containers
  const scrollToBottom = () => {
    messagesEndRef1.current?.scrollIntoView({ behavior: 'smooth' });
    messagesEndRef2.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Load conversation between two users
  const loadConversation = async () => {
    if (!activeConversation.user1 || !activeConversation.user2) {
      setError('Please enter both user IDs');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await dispatch(fetchConversation({
        userId: activeConversation.user1,
        otherUserId: activeConversation.user2,
        includeDeleted: isAdmin && showDeletedMessages
      })).unwrap();

      setMessages(result.messages || []);
      scrollToBottom();
    } catch (err) {
      setError(`Failed to load conversation: ${err.message}`);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  // Send message from user 1
  const sendMessageFromUser1 = async () => {
    if (!newMessage1.trim() || !activeConversation.user1 || !activeConversation.user2) {
      return;
    }

    try {
      await dispatch(sendMessage({
        senderId: activeConversation.user1,
        recipientId: activeConversation.user2,
        content: newMessage1,
        messageType: 'GENERAL'
      })).unwrap();

      setNewMessage1('');
      // Message will be added via SSE
    } catch (err) {
      setError(`Failed to send message: ${err.message}`);
    }
  };

  // Send message from user 2
  const sendMessageFromUser2 = async () => {
    if (!newMessage2.trim() || !activeConversation.user1 || !activeConversation.user2) {
      return;
    }

    try {
      await dispatch(sendMessage({
        senderId: activeConversation.user2,
        recipientId: activeConversation.user1,
        content: newMessage2,
        messageType: 'GENERAL'
      })).unwrap();

      setNewMessage2('');
      // Message will be added via SSE
    } catch (err) {
      setError(`Failed to send message: ${err.message}`);
    }
  };

  // Delete individual message
  const handleDeleteMessage = async (messageId, senderId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) {
      return;
    }

    try {
      await dispatch(deleteMessage({
        messageId,
        userId: senderId
      })).unwrap();

      // Update local messages state
      setMessages(prev => prev.map(msg =>
        msg.uuid === messageId ? { ...msg, isDeleted: true } : msg
      ));
    } catch (err) {
      setError(`Failed to delete message: ${err.message}`);
    }
  };

  // Delete thread (if threadId exists)
  const handleDeleteThread = async (threadId) => {
    if (!threadId) {
      setError('No thread ID available for deletion');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this entire thread? This will delete all messages in the thread.')) {
      return;
    }

    const conversationId = `conv_${[activeConversation.user1, activeConversation.user2].sort().join('_')}`;

    try {
      await dispatch(deleteThread({
        conversationId,
        threadId,
        userId: activeConversation.user1
      })).unwrap();

      // Update local messages state
      setMessages(prev => prev.map(msg =>
        msg.threadId === threadId ? { ...msg, isDeleted: true } : msg
      ));
    } catch (err) {
      setError(`Failed to delete thread: ${err.message}`);
    }
  };

  // Delete entire conversation
  const handleDeleteConversation = async () => {
    if (!window.confirm('Are you sure you want to delete this entire conversation? This will delete all messages between these users.')) {
      return;
    }

    const conversationId = `conv_${[activeConversation.user1, activeConversation.user2].sort().join('_')}`;

    try {
      await dispatch(deleteConversation({
        conversationId,
        userId: activeConversation.user1
      })).unwrap();

      // Clear local messages
      setMessages([]);
    } catch (err) {
      setError(`Failed to delete conversation: ${err.message}`);
    }
  };

  // Admin hard delete handlers
  const showAdminConfirmation = (type, messageId = '', threadId = '') => {
    const conversationId = `conv_${[activeConversation.user1, activeConversation.user2].sort().join('_')}`;
    setAdminConfirmation({
      show: true,
      type,
      messageId,
      conversationId,
      threadId,
      confirmText: ''
    });
  };

  const handleAdminConfirmation = async () => {
    if (adminConfirmation.confirmText !== 'DELETE') {
      setError('You must type "DELETE" to confirm permanent deletion');
      return;
    }

    try {
      switch (adminConfirmation.type) {
        case 'message':
          await dispatch(adminHardDeleteMessage({
            messageId: adminConfirmation.messageId
          })).unwrap();
          // Remove message completely from local state
          setMessages(prev => prev.filter(msg => msg.uuid !== adminConfirmation.messageId));
          break;

        case 'thread':
          await dispatch(adminHardDeleteThread({
            conversationId: adminConfirmation.conversationId,
            threadId: adminConfirmation.threadId
          })).unwrap();
          // Remove all messages in thread from local state
          setMessages(prev => prev.filter(msg => msg.threadId !== adminConfirmation.threadId));
          break;

        case 'conversation':
          await dispatch(adminHardDeleteConversation({
            conversationId: adminConfirmation.conversationId
          })).unwrap();
          // Clear all messages from local state
          setMessages([]);
          break;

        default:
          break;
      }

      setAdminConfirmation({
        show: false,
        type: '',
        messageId: '',
        conversationId: '',
        threadId: '',
        confirmText: ''
      });
    } catch (err) {
      setError(`Failed to perform admin hard delete: ${err.message}`);
    }
  };

  const cancelAdminConfirmation = () => {
    setAdminConfirmation({
      show: false,
      type: '',
      messageId: '',
      conversationId: '',
      threadId: '',
      confirmText: ''
    });
  };

  // Quick setup with test user IDs
  const setupTestConversation = (type) => {
    switch (type) {
      case 'va_bc':
        setActiveConversation({
          user1: testUsers.voiceActor,
          user2: testUsers.bookCreator,
          user1Type: 'voiceActor',
          user2Type: 'bookCreator'
        });
        break;
      case 'va_admin':
        setActiveConversation({
          user1: testUsers.voiceActor,
          user2: testUsers.admin,
          user1Type: 'voiceActor',
          user2Type: 'admin'
        });
        break;
      case 'bc_admin':
        setActiveConversation({
          user1: testUsers.bookCreator,
          user2: testUsers.admin,
          user1Type: 'bookCreator',
          user2Type: 'admin'
        });
        break;
      default:
        break;
    }
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  // Get user display name using role normalization
  const getUserDisplayName = (userId, userType) => {
    const roleLabel = getRoleLabel(normalizeRole(userType));
    return `${roleLabel || 'User'} (${userId.substring(0, 8)}...)`;
  };

  return (
    <div className="messaging-test">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <Header as="h2" style={{ margin: 0 }}>
          <Icon name="comments" />
          Real-Time Messaging Test
        </Header>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Button
            as={Link}
            to="/enhanced-messaging-test"
            color="blue"
            icon="lab"
            content="Enhanced Messaging Test Lab"
            size="small"
          />
          <Label color={isAdmin ? 'red' : 'blue'} size="large">
            <Icon name={isAdmin ? 'shield' : 'user'} />
            Active User Role: {userRole || 'Unknown'}
          </Label>
        </div>
      </div>

      {error && (
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {/* Test User Configuration */}
      <Segment>
        <Header as="h3">Test User Configuration</Header>
        <div className="form-row">
          <div className="form-field">
            <span className="field-label">Voice Actor User ID</span>
            <Input
              placeholder="Enter Voice Actor UUID"
              value={testUsers.voiceActor}
              onChange={(e) => setTestUsers(prev => ({ ...prev, voiceActor: e.target.value }))}
            />
          </div>
          <div className="form-field">
            <span className="field-label">Book Creator User ID</span>
            <Input
              placeholder="Enter Book Creator UUID"
              value={testUsers.bookCreator}
              onChange={(e) => setTestUsers(prev => ({ ...prev, bookCreator: e.target.value }))}
            />
          </div>
          <div className="form-field">
            <span className="field-label">Admin User ID</span>
            <Input
              placeholder="Enter Admin UUID"
              value={testUsers.admin}
              onChange={(e) => setTestUsers(prev => ({ ...prev, admin: e.target.value }))}
            />
          </div>
          <div className="form-field">
            <span className="field-label">&nbsp;</span>
            <div style={{ height: '36px' }}></div>
          </div>
        </div>
      </Segment>

      {/* Conversation Setup */}
      <Segment>
        <Header as="h3">Conversation Setup</Header>
        <div className="form-row">
          <div className="form-field">
            <span className="field-label">User 1 ID</span>
            <Input
              placeholder="Enter User 1 UUID"
              value={activeConversation.user1}
              onChange={(e) => setActiveConversation(prev => ({ ...prev, user1: e.target.value }))}
            />
          </div>
          <div className="form-field">
            <span className="field-label">User 1 Type</span>
            <Dropdown
              placeholder="Select type"
              fluid
              selection
              options={getUserTypeOptions()}
              value={activeConversation.user1Type}
              onChange={(e, { value }) => setActiveConversation(prev => ({ ...prev, user1Type: value }))}
            />
          </div>
          <div className="form-field">
            <span className="field-label">User 2 ID</span>
            <Input
              placeholder="Enter User 2 UUID"
              value={activeConversation.user2}
              onChange={(e) => setActiveConversation(prev => ({ ...prev, user2: e.target.value }))}
            />
          </div>
          <div className="form-field">
            <span className="field-label">User 2 Type</span>
            <Dropdown
              placeholder="Select type"
              fluid
              selection
              options={getUserTypeOptions()}
              value={activeConversation.user2Type}
              onChange={(e, { value }) => setActiveConversation(prev => ({ ...prev, user2Type: value }))}
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-field">
            <span className="field-label">&nbsp;</span>
            <Button
              primary
              onClick={loadConversation}
              loading={loading}
              disabled={!activeConversation.user1 || !activeConversation.user2}
              fluid
            >
              Load Conversation
            </Button>
          </div>
          <div className="form-field">
            <span className="field-label">&nbsp;</span>
            <Button
              onClick={() => setupTestConversation('va_bc')}
              disabled={!testUsers.voiceActor || !testUsers.bookCreator}
              fluid
            >
              VA ↔ BC
            </Button>
          </div>
          <div className="form-field">
            <span className="field-label">&nbsp;</span>
            <Button
              onClick={() => setupTestConversation('va_admin')}
              disabled={!testUsers.voiceActor || !testUsers.admin}
              fluid
            >
              VA ↔ Admin
            </Button>
          </div>
          <div className="form-field">
            <span className="field-label">&nbsp;</span>
            <Button
              onClick={() => setupTestConversation('bc_admin')}
              disabled={!testUsers.bookCreator || !testUsers.admin}
              fluid
            >
              BC ↔ Admin
            </Button>
          </div>
        </div>
      </Segment>

      {/* Real-Time Messaging Interface */}
      {activeConversation.user1 && activeConversation.user2 && (
        <Grid columns={2} divided>
          <Grid.Row>
            <Grid.Column>
              <Card fluid>
                <Card.Content>
                  <Card.Header>
                    <Icon name="user" />
                    {getUserDisplayName(activeConversation.user1, activeConversation.user1Type)}
                  </Card.Header>
                </Card.Content>
                <Card.Content>
                  <div className="message-container" id="messages-user1">
                    {messages
                      .filter(message => showDeletedMessages || !message.isDeleted)
                      .map((message, index) => (
                        <div
                          key={`user1-${message.uuid || index}`}
                          className={`message-bubble ${message.sender?.userId === activeConversation.user1 ? 'sent' : 'received'
                            } ${message.isDeleted ? 'deleted' : ''}`}
                        >
                          <div className="message-content">
                            {message.isDeleted ? '[Message deleted]' : message.content}
                          </div>
                          <div className="message-actions">
                            <div className="message-time">
                              {formatTime(message.createdAt)}
                            </div>
                            {!message.isDeleted && message.sender?.userId === activeConversation.user1 && (
                              <Button
                                size="mini"
                                icon="trash"
                                color="red"
                                onClick={() => handleDeleteMessage(message.uuid, message.sender.userId)}
                                title="Delete message"
                              />
                            )}
                            {isAdmin && message.isDeleted && showDeletedMessages && (
                              <Button
                                size="mini"
                                icon="bomb"
                                color="red"
                                onClick={() => showAdminConfirmation('message', message.uuid)}
                                title="Admin: Permanently delete this message"
                                className="admin-hard-delete-btn"
                              />
                            )}
                          </div>
                        </div>
                      ))}
                    {messages.length > 0 && messages.filter(message => showDeletedMessages || !message.isDeleted).length === 0 && isAdmin && (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#666', fontStyle: 'italic' }}>
                        <Icon name="eye slash" />
                        No visible messages. Toggle "Show Deleted Messages" in Admin Controls to see soft-deleted content.
                      </div>
                    )}
                    {messages.length === 0 && (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#999', fontStyle: 'italic' }}>
                        <Icon name="comments outline" />
                        No messages in this conversation yet.
                      </div>
                    )}
                    <div ref={messagesEndRef1} />
                  </div>
                </Card.Content>
                <Card.Content extra>
                  <Form onSubmit={(e) => { e.preventDefault(); sendMessageFromUser1(); }}>
                    <Form.Group>
                      <Form.Field width={12}>
                        <TextArea
                          placeholder="Type a message..."
                          value={newMessage1}
                          onChange={(e) => setNewMessage1(e.target.value)}
                          rows={2}
                        />
                      </Form.Field>
                      <Form.Field width={4}>
                        <Button
                          primary
                          type="submit"
                          disabled={!newMessage1.trim()}
                          fluid
                        >
                          Send
                        </Button>
                      </Form.Field>
                    </Form.Group>
                  </Form>
                </Card.Content>
              </Card>
            </Grid.Column>

            <Grid.Column>
              <Card fluid>
                <Card.Content>
                  <Card.Header>
                    <Icon name="user" />
                    {getUserDisplayName(activeConversation.user2, activeConversation.user2Type)}
                  </Card.Header>
                </Card.Content>
                <Card.Content>
                  <div className="message-container" id="messages-user2">
                    {messages
                      .filter(message => showDeletedMessages || !message.isDeleted)
                      .map((message, index) => (
                        <div
                          key={`user2-${message.uuid || index}`}
                          className={`message-bubble ${message.sender?.userId === activeConversation.user2 ? 'sent' : 'received'
                            } ${message.isDeleted ? 'deleted' : ''}`}
                        >
                          <div className="message-content">
                            {message.isDeleted ? '[Message deleted]' : message.content}
                          </div>
                          <div className="message-actions">
                            <div className="message-time">
                              {formatTime(message.createdAt)}
                            </div>
                            {!message.isDeleted && message.sender?.userId === activeConversation.user2 && (
                              <Button
                                size="mini"
                                icon="trash"
                                color="red"
                                onClick={() => handleDeleteMessage(message.uuid, message.sender.userId)}
                                title="Delete message"
                              />
                            )}
                            {isAdmin && message.isDeleted && showDeletedMessages && (
                              <Button
                                size="mini"
                                icon="bomb"
                                color="red"
                                onClick={() => showAdminConfirmation('message', message.uuid)}
                                title="Admin: Permanently delete this message"
                                className="admin-hard-delete-btn"
                              />
                            )}
                          </div>
                        </div>
                      ))}
                    {messages.length > 0 && messages.filter(message => showDeletedMessages || !message.isDeleted).length === 0 && isAdmin && (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#666', fontStyle: 'italic' }}>
                        <Icon name="eye slash" />
                        No visible messages. Toggle "Show Deleted Messages" in Admin Controls to see soft-deleted content.
                      </div>
                    )}
                    {messages.length === 0 && (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#999', fontStyle: 'italic' }}>
                        <Icon name="comments outline" />
                        No messages in this conversation yet.
                      </div>
                    )}
                    <div ref={messagesEndRef2} />
                  </div>
                </Card.Content>
                <Card.Content extra>
                  <Form onSubmit={(e) => { e.preventDefault(); sendMessageFromUser2(); }}>
                    <Form.Group>
                      <Form.Field width={12}>
                        <TextArea
                          placeholder="Type a message..."
                          value={newMessage2}
                          onChange={(e) => setNewMessage2(e.target.value)}
                          rows={2}
                        />
                      </Form.Field>
                      <Form.Field width={4}>
                        <Button
                          primary
                          type="submit"
                          disabled={!newMessage2.trim()}
                          fluid
                        >
                          Send
                        </Button>
                      </Form.Field>
                    </Form.Group>
                  </Form>
                </Card.Content>
              </Card>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      )}

      {/* Delete Controls */}
      {activeConversation.user1 && activeConversation.user2 && messages.length > 0 && (
        <Segment>
          <Header as="h3" color="red">
            <Icon name="trash" />
            Delete Controls
          </Header>
          <div className="form-row">
            <div className="form-field">
              <span className="field-label">Delete Thread</span>
              <Button
                color="orange"
                icon="trash alternate"
                content="Delete Thread"
                onClick={() => {
                  const threadIds = [...new Set(messages.map(m => m.threadId).filter(Boolean))];
                  if (threadIds.length > 0) {
                    handleDeleteThread(threadIds[0]);
                  } else {
                    setError('No threads found in this conversation');
                  }
                }}
                disabled={!messages.some(m => m.threadId)}
                title="Delete all messages in the first thread found"
              />
            </div>
            <div className="form-field">
              <span className="field-label">Delete Conversation</span>
              <Button
                color="red"
                icon="trash"
                content="Delete Entire Conversation"
                onClick={handleDeleteConversation}
                title="Delete all messages in this conversation"
              />
            </div>
            <div className="form-field">
              <span className="field-label">Clear Local View</span>
              <Button
                color="grey"
                icon="eraser"
                content="Clear Messages"
                onClick={() => setMessages([])}
                title="Clear messages from local view only"
              />
            </div>
            <div className="form-field">
              <span className="field-label">&nbsp;</span>
              <div style={{ height: '36px' }}></div>
            </div>
          </div>
        </Segment>
      )}

      {/* Admin-Only Hard Delete Controls */}
      {isAdmin && activeConversation.user1 && activeConversation.user2 && (
        <Segment className="admin-hard-delete-section">
          <Header as="h3" color="red">
            <Icon name="warning sign" />
            Admin Hard Delete Controls
            <Header.Subheader>
              ⚠️ PERMANENT DELETION - These actions cannot be undone and will completely remove data from the database
            </Header.Subheader>
          </Header>
          <Message warning>
            <Message.Header>Admin Only - Destructive Operations</Message.Header>
            <p>
              Hard delete operations permanently remove messages from the Neo4j database using CASCADE DELETE.
              This is intended for testing and data cleanup purposes only.
            </p>
          </Message>
          <div className="form-row">
            <div className="form-field">
              <span className="field-label">Show Deleted Messages</span>
              <Button
                toggle
                active={showDeletedMessages}
                onClick={() => setShowDeletedMessages(!showDeletedMessages)}
                color={showDeletedMessages ? 'green' : 'grey'}
                icon={showDeletedMessages ? 'eye' : 'eye slash'}
                content={showDeletedMessages ? 'Showing Deleted' : 'Hide Deleted'}
                title="Toggle visibility of soft-deleted messages for admin hard delete"
              />
            </div>
            <div className="form-field">
              <span className="field-label">Hard Delete Thread</span>
              <Button
                color="red"
                icon="bomb"
                content="HARD DELETE Thread"
                onClick={() => {
                  const threadIds = [...new Set(messages.map(m => m.threadId).filter(Boolean))];
                  if (threadIds.length > 0) {
                    showAdminConfirmation('thread', '', threadIds[0]);
                  } else {
                    setError('No threads found in this conversation');
                  }
                }}
                disabled={!messages.some(m => m.threadId)}
                title="Permanently delete all messages in the first thread found"
                className="admin-hard-delete-btn"
              />
            </div>
            <div className="form-field">
              <span className="field-label">Hard Delete Conversation</span>
              <Button
                color="red"
                icon="bomb"
                content="HARD DELETE Conversation"
                onClick={() => showAdminConfirmation('conversation')}
                title="Permanently delete all messages in this conversation"
                className="admin-hard-delete-btn"
              />
            </div>
            <div className="form-field">
              <span className="field-label">Current User Role</span>
              <Label color="red" size="large">
                <Icon name="shield" />
                Admin ({userRole})
              </Label>
            </div>
          </div>
        </Segment>
      )}

      {/* Admin Confirmation Modal */}
      <Modal
        open={adminConfirmation.show}
        onClose={cancelAdminConfirmation}
        size="small"
        className="admin-confirmation-modal"
      >
        <Modal.Header>
          <Icon name="warning sign" color="red" />
          Confirm Permanent Deletion
        </Modal.Header>
        <Modal.Content>
          <Message error>
            <Message.Header>⚠️ DESTRUCTIVE OPERATION WARNING ⚠️</Message.Header>
            <Message.List>
              <Message.Item>This action will PERMANENTLY delete data from the database</Message.Item>
              <Message.Item>This operation CANNOT be undone</Message.Item>
              <Message.Item>All related data will be removed via CASCADE DELETE</Message.Item>
              <Message.Item>This is intended for testing and cleanup purposes only</Message.Item>
            </Message.List>
          </Message>

          <div style={{ marginTop: '20px', marginBottom: '20px' }}>
            <strong>Operation:</strong> Hard Delete {adminConfirmation.type}
            {adminConfirmation.type === 'message' && (
              <div><strong>Message ID:</strong> {adminConfirmation.messageId}</div>
            )}
            {adminConfirmation.type === 'thread' && (
              <div><strong>Thread ID:</strong> {adminConfirmation.threadId}</div>
            )}
            {adminConfirmation.type === 'conversation' && (
              <div><strong>Conversation ID:</strong> {adminConfirmation.conversationId}</div>
            )}
          </div>

          <Form>
            <Form.Field>
              <label style={{ color: 'red', fontWeight: 'bold' }}>
                Type "DELETE" to confirm permanent deletion:
              </label>
              <Input
                placeholder="Type DELETE to confirm"
                value={adminConfirmation.confirmText}
                onChange={(e) => setAdminConfirmation(prev => ({
                  ...prev,
                  confirmText: e.target.value
                }))}
                style={{
                  border: adminConfirmation.confirmText === 'DELETE' ? '2px solid green' : '2px solid red'
                }}
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={cancelAdminConfirmation}>
            <Icon name="remove" />
            Cancel
          </Button>
          <Button
            color="red"
            onClick={handleAdminConfirmation}
            disabled={adminConfirmation.confirmText !== 'DELETE'}
          >
            <Icon name="bomb" />
            PERMANENTLY DELETE
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Connection Status */}
      <Segment>
        <Label color={eventSourceRef.current?.readyState === 1 ? 'green' : 'red'}>
          <Icon name={eventSourceRef.current?.readyState === 1 ? 'wifi' : 'remove'} />
          Real-time Connection: {eventSourceRef.current?.readyState === 1 ? 'Connected' : 'Disconnected'}
        </Label>
        <Label color="blue">
          <Icon name="comments" />
          Messages: {messages.length}
        </Label>
        <Label color="purple">
          <Icon name="hashtag" />
          Threads: {[...new Set(messages.map(m => m.threadId).filter(Boolean))].length}
        </Label>
        {isAdmin && (
          <Label color="red">
            <Icon name="shield" />
            Admin Mode: Hard Delete Available
          </Label>
        )}
      </Segment>
    </div>
  );
};

export default MessagingTest;
